import * as React from "react";
import { Link } from "react-router";
import { MailCheck } from "lucide-react";
import { EMAIL_CODE_LENGTH, emailCodeSchema, startSignUpSchema } from "@taskiq/shared";
import { AuthLayout } from "@/components/AuthLayout";
import { TurnstileWidget } from "@/components/auth/TurnstileWidget";
import { CodeInput } from "@/components/auth/CodeInput";
import { OAuthButtons } from "@/components/auth/OAuthButtons";
import { Button } from "@/components/ui/Button";
import { PhoneField } from "@/components/ui/PhoneField";
import { Field, Input, Form } from "@/components/ui/Field";
import { FormMessage } from "@/components/ui/Feedback";
import { TextLink } from "@/components/ui/TextLink";
import { useAuth } from "@/lib/auth-context";
import { describeAuthError, fieldErrorsFrom } from "@/lib/auth-errors";
import { PLACEHOLDER } from "@/lib/placeholders";
import { useTurnstile } from "@/lib/turnstile";

/**
 * Self sign-up, steps one and two: prove who you are and prove you can read the
 * address.
 *
 * Steps three and four (password, then company and first site) live on
 * `/onboarding`, and the split is not arbitrary. **Everything on this screen
 * happens without a session; everything on that one requires one.** Verifying
 * the code is the moment a session comes into existence, so it is the natural
 * seam, and it is also the only one that survives a page refresh: from step
 * three onwards the state lives in Supabase rather than in this component.
 *
 * There is no explicit navigation at the end. `verifyEmailCode` produces a
 * session, the listener in `AuthProvider` picks it up, and `RedirectIfAuthed`
 * (which wraps this route) sends the user to `/onboarding` or straight to the
 * console if they turn out to have a workspace already. Routing from the auth
 * state rather than from the submit handler is what makes "signed up with an
 * address that was already invited" land in the right company for free.
 */

/**
 * Long enough that a slow mail server is not mistaken for a broken button, short
 * enough that somebody who really did not get the mail is not stuck. GoTrue's own
 * per-address email rate limit is the real floor; asking again inside it returns
 * `over_email_send_rate_limit`, which is a worse experience than a disabled
 * button with a number on it.
 */
const RESEND_COOLDOWN_SECONDS = 45;

/** Survives a refresh on step two, which otherwise loses the address entirely. */
const PENDING_EMAIL_KEY = "taskiq.signup.email";

export function SignUp() {
  const { startSignUp, verifyEmailCode, resendEmailCode } = useAuth();

  const [pendingEmail, setPendingEmail] = React.useState<string | null>(() => {
    try {
      return sessionStorage.getItem(PENDING_EMAIL_KEY);
    } catch {
      return null;
    }
  });

  function rememberEmail(value: string | null) {
    setPendingEmail(value);
    try {
      if (value) sessionStorage.setItem(PENDING_EMAIL_KEY, value);
      else sessionStorage.removeItem(PENDING_EMAIL_KEY);
    } catch {
      /* private mode: the step still works, it just will not survive a reload */
    }
  }

  if (pendingEmail) {
    return (
      <VerifyStep
        email={pendingEmail}
        onVerify={verifyEmailCode}
        onResend={resendEmailCode}
        onChangeEmail={() => rememberEmail(null)}
      />
    );
  }

  return <IdentityStep onSubmitted={rememberEmail} onStart={startSignUp} />;
}

/* ──────────────────── Step 1: name, email, and a phone if they have one ─────────────────── */

function IdentityStep({
  onStart,
  onSubmitted,
}: {
  onStart: (
    input: { email: string; full_name: string; phone?: string },
    captchaToken?: string
  ) => Promise<void>;
  onSubmitted: (email: string) => void;
}) {
  const [values, setValues] = React.useState({ full_name: "", email: "", phone: "" });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const captcha = useTurnstile();

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    // The same zod schema the rest of the codebase uses, so the form can never
    // submit something that was always going to be rejected.
    const parsed = startSignUpSchema.safeParse(values);
    if (!parsed.success) {
      setFieldErrors(fieldErrorsFrom(parsed.error));
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    try {
      await onStart(parsed.data, captcha.token);
      onSubmitted(parsed.data.email.trim().toLowerCase());
    } catch (err) {
      setFormError(describeAuthError(err));
      // Spent regardless of what failed - see `TurnstileState.reset`.
      captcha.reset();
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Set up TaskIQ for your crews. No card, no sales call."
      // The top of the funnel, same as sign-in's own back arrow - both are
      // reached from the marketing nav, so both leave the same way.
      back={{ to: "/", label: "Back to site" }}
      footer={
        <>
          Already have an account? <TextLink to="/login">Sign in</TextLink>
        </>
      }
    >
      <Form onSubmit={onSubmit}>
        <Field label="Your name" htmlFor="full_name" required error={fieldErrors.full_name}>
          <Input
            id="full_name"
            name="full_name"
            inputSize="md"
            autoComplete="name"
            autoFocus
            placeholder={PLACEHOLDER.fullName}
            invalid={Boolean(fieldErrors.full_name)}
            value={values.full_name}
            onChange={(e) => setValues((v) => ({ ...v, full_name: e.target.value }))}
          />
        </Field>

        <Field label="Work email" htmlFor="email" required error={fieldErrors.email}>
          <Input
            id="email"
            name="email"
            type="email"
            inputSize="md"
            autoComplete="username email"
            placeholder={PLACEHOLDER.email}
            invalid={Boolean(fieldErrors.email)}
            value={values.email}
            onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
          />
        </Field>

        <Field label="Mobile number (optional)" htmlFor="phone" error={fieldErrors.phone}>
          <PhoneField
            id="phone"
            name="phone"
            invalid={Boolean(fieldErrors.phone)}
            value={values.phone}
            onChange={(phone) => setValues((v) => ({ ...v, phone }))}
          />
        </Field>

        {/* Inline, never a toast. A toast auto-dismisses and a failed sign-up
            has to stay on screen until it is dealt with. */}
        <FormMessage>{formError}</FormMessage>

        {/* The most abusable form in the product: it creates a GoTrue identity
            and sends mail against the project's shared quota, for an entirely
            anonymous caller, on an endpoint none of our own middleware sees. */}
        <TurnstileWidget
          onToken={captcha.onToken}
          onUnavailable={captcha.onUnavailable}
          resetKey={captcha.resetKey}
        />

        <Button type="submit" size="lg" block loading={submitting} className="auth-submit">
          Continue
        </Button>
      </Form>

      <div className="auth-resend">
        <OAuthButtons label="Or sign up with" />
      </div>
    </AuthLayout>
  );
}

/* ────────────────────────── Step 2: the emailed code ────────────────────────── */

function VerifyStep({
  email,
  onVerify,
  onResend,
  onChangeEmail,
}: {
  email: string;
  onVerify: (email: string, code: string) => Promise<void>;
  onResend: (email: string, captchaToken?: string) => Promise<void>;
  onChangeEmail: () => void;
}) {
  const [code, setCode] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(RESEND_COOLDOWN_SECONDS);
  const captcha = useTurnstile();

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const submit = React.useCallback(
    async (value: string) => {
      setFormError(null);
      setNotice(null);

      const parsed = emailCodeSchema.safeParse({ email, code: value });
      if (!parsed.success) {
        setFormError(parsed.error.issues[0]?.message ?? "Check the code and try again.");
        return;
      }

      setSubmitting(true);
      try {
        // No navigate() on success. The session that appears here is what moves
        // the user on, via AuthProvider's listener and RedirectIfAuthed.
        await onVerify(parsed.data.email, parsed.data.code);
      } catch (err) {
        setFormError(describeAuthError(err));
        setCode("");
        setSubmitting(false);
      }
    },
    [email, onVerify]
  );

  async function resend() {
    setFormError(null);
    setCooldown(RESEND_COOLDOWN_SECONDS);
    try {
      await onResend(email, captcha.token);
      setNotice("Sent. Check your email again, including spam.");
    } catch (err) {
      setFormError(describeAuthError(err));
    } finally {
      captcha.reset();
    }
  }

  return (
    <AuthLayout
      title="Check your email"
      subtitle={
        <>
          We sent a {EMAIL_CODE_LENGTH}-digit code to <strong className="ink">{email}</strong>.
        </>
      }
      // The back arrow is the "wrong address, let me fix it" control here -
      // it does not navigate, it clears the pending email and drops the
      // component back to `IdentityStep`. This replaced a text link with the
      // same handler in the footer; one control for one action.
      back={{ onClick: onChangeEmail, label: "Use a different email address" }}
    >
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          void submit(code);
        }}
        gap="loose"
      >
        <div className="auth-callout">
          <MailCheck />
          The code expires in an hour and can only be used once.
        </div>

        <CodeInput
          value={code}
          onChange={setCode}
          // Submits itself the moment the sixth digit lands, so pasting a code
          // is one action rather than a paste and a press.
          onComplete={(value) => void submit(value)}
          invalid={Boolean(formError)}
          disabled={submitting}
          autoFocus
        />

        <FormMessage>{formError}</FormMessage>
        {notice && !formError && <p className="auth-ok">{notice}</p>}

        <Button
          type="submit"
          size="lg"
          block
          loading={submitting}
          disabled={code.length < EMAIL_CODE_LENGTH}
        >
          Verify and continue
        </Button>

        {/* Only while the resend is reachable. Entering the code is not
            challenged - GoTrue does not verify a captcha on `/verify` and the
            six-digit space plus the one-hour expiry are the guard there, the
            same call the main product makes about its 2FA step. A challenge
            sitting above a code field would say otherwise. */}
        {cooldown <= 0 && (
          <TurnstileWidget
            onToken={captcha.onToken}
            onUnavailable={captcha.onUnavailable}
            resetKey={captcha.resetKey}
          />
        )}

        <div className="auth-centre-note">
          {cooldown > 0 ? (
            <span>
              Didn't get it? You can ask again in <span className="tnum">{cooldown}</span>s
            </span>
          ) : (
            <button
              type="button"
              onClick={() => void resend()}
              className="text-link text-link--accent"
            >
              Send a new code
            </button>
          )}
        </div>
      </Form>
    </AuthLayout>
  );
}
